import React, { useEffect, useState } from 'react';
import { getCurrencies, refreshRates } from '../../services/adminApi';
import { HiOutlineRefresh } from 'react-icons/hi';

interface Currency {
  id: number;
  code: string;
  name: string;
  rate: string;
  symbol: string;
  updated_at?: string;
}

const AdminCurrency: React.FC = () => {
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const fetchData = async () => {
    try {
      const res = await getCurrencies();
      const data = Array.isArray(res.data) ? res.data : res.data.results || [];
      setCurrencies(data);
    } catch {
      setError('Failed to load currencies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    setError('');
    try {
      await refreshRates();
      setMessage('Exchange rates refreshed successfully');
      fetchData();
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to refresh rates');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) return <div className="admin-spinner" />;

  return (
    <div>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Currency Rates</h1>
        <button
          className="admin-btn admin-btn-primary"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          <HiOutlineRefresh size={18} className={refreshing ? 'admin-spin' : ''} />
          {refreshing ? 'Refreshing...' : 'Refresh Rates'}
        </button>
      </div>

      {message && <div className="admin-alert admin-alert-success">{message}</div>}
      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Symbol</th>
              <th>Rate</th>
              <th>Last Updated</th>
            </tr>
          </thead>
          <tbody>
            {currencies.length === 0 ? (
              <tr>
                <td colSpan={5} className="admin-table-empty">No currencies configured</td>
              </tr>
            ) : (
              currencies.map((c) => (
                <tr key={c.id}>
                  <td><strong>{c.code}</strong></td>
                  <td>{c.name}</td>
                  <td>{c.symbol}</td>
                  <td>{c.rate}</td>
                  <td>{c.updated_at ? new Date(c.updated_at).toLocaleString() : '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminCurrency;
